import { Form, Formik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import { HtmlContent } from '../../global-component-styles';
import GlobalStyles from '../../global-styles';
import API from '../../utils/api';
import {
  apiReponseError, apiRequestError, isValidJSONString, setAllFieldTouched
} from '../../utils/helper';
import { Box, Button, Grid, Paper, TextField, Typography } from '../../utils/material-ui-core';

interface PhoneType {
  phone: string;
}

interface PhoneResultOutPut {
  countryCode: string;
  phoneNumber: string;
  nationalFormat: string;
  carrier: {
    mobileCountryCode: string;
    mobileNetworkCode: string;
    name: string;
    type: string;
    error_code: string;
  };
}

const phoneValidationDefaultValue = {
  phone: "",
};

const phoneRegExp = /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

const phoneFormValidationSchema = Yup.object().shape({
  phone: Yup.string().matches(phoneRegExp, "Phone number is not valid").required("Phone is required.").typeError("That doesn't look like a phone number").min(10),
});

const PhoneValidator = () => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const formReference = useRef(null) as any;

  const defaultPhoneResultOutput: PhoneResultOutPut = {
    countryCode: "",
    phoneNumber: "",
    nationalFormat: "",
    carrier: {
      mobileCountryCode: "",
      mobileNetworkCode: "",
      name: "",
      type: "",
      error_code: "",
    },
  };
  const [loading, setLoading] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState<PhoneResultOutPut>(defaultPhoneResultOutput);
  const [errorMessage, setErrorMessage] = useState("");
  const [initialPhoneValidationFormValues, setInitialPhoneValidationFormValues] = useState(phoneValidationDefaultValue);

  const submitPhoneValidation = async (values: PhoneType, actions: any) => {
    const payload = {};

    setShowResult(false);
    setErrorMessage("");
    setResult(defaultPhoneResultOutput);
    setLoading(true);
    try {
      await API.post("/api/validation/phoneinfo/" + values.phone, payload)
        .then(async (response) => {
          try {
            if (isValidJSONString(response.data)) {
              const output = response.data.output;

              if (output !== null) {
                const jsonOutput: any = JSON.parse(output);
                const phoneResultOutPut: PhoneResultOutPut = {
                  countryCode: jsonOutput.country_code,
                  phoneNumber: jsonOutput.phone_number,
                  nationalFormat: jsonOutput.national_format,
                  carrier: {
                    mobileCountryCode: jsonOutput.carrier.mobile_country_code,
                    mobileNetworkCode: jsonOutput.carrier.mobile_network_code,
                    name: jsonOutput.carrier.name,
                    type: jsonOutput.carrier.type,
                    error_code: jsonOutput.carrier.error_code,
                  },
                };
                setResult(phoneResultOutPut);
                setShowResult(true);
              } else {
                setErrorMessage("Invalid phone number.");
                setShowResult(true);
              }
              setLoading(false);
            }
          } catch (e) {
            setErrorMessage(apiRequestError(e, "Something was wrong in our system."));
            setShowResult(true);
            setLoading(false);
          }
        })
        .catch((error) => {
          setErrorMessage(apiReponseError(error, "."));
          setShowResult(true);
          setLoading(false);
        });
    } catch (e) {
      setErrorMessage(apiRequestError(e, "Something was wrong in our system."));
      setShowResult(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    const initialPageAsync = async () => {
      setInitialPhoneValidationFormValues(phoneValidationDefaultValue);
    };
    initialPageAsync();
  }, []);

  return (
    <Box className={pageStyles.validatorContainer}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box className={globalStyles.topic}>Phone Validator</Box>
        </Grid>
      </Grid>

      <Box p={2} className={pageStyles.inputContainer}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={6}>
            <Formik innerRef={formReference} initialValues={initialPhoneValidationFormValues} validationSchema={phoneFormValidationSchema} validateOnBlur={true} validateOnChange={true} onSubmit={submitPhoneValidation}>
              {({ values, errors, handleChange, handleReset, setSubmitting, touched, handleBlur, isSubmitting }) => (
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    submitPhoneValidation(values, { setSubmitting });
                  }}
                >
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={6} lg={6}>
                      <TextField
                        required
                        type="phone"
                        id="phone"
                        label="Phone"
                        value={values.phone}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth={true}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        error={touched.phone && Boolean(errors.phone)}
                        helperText={errors.phone && touched.phone ? errors.phone : null}
                      />
                    </Grid>
                  </Grid>

                  <Box p={1}>&nbsp;</Box>
                  <Box p={3} pt={3} display="flex" justifyContent="center">
                    <Button
                      type="button"
                      id="button-clear-phone"
                      color="default"
                      disabled={loading}
                      className={globalStyles.buttonDefault}
                      onClick={() => {
                        handleReset();
                        setShowResult(false);
                      }}
                    >
                      Clear
                    </Button>
                    <Button
                      type="submit"
                      id="button-save-phone"
                      variant="contained"
                      color="secondary"
                      className={globalStyles.buttonDefault}
                      onClick={() => {
                        setAllFieldTouched(formReference, "", values);
                      }}
                    >
                      {loading ? "Checking..." : "Validate"}
                    </Button>
                  </Box>
                </Form>
              )}
            </Formik>
          </Grid>
          <Grid item xs={12} md={6} lg={6}>
            {showResult ? (
              <>
                <Paper elevation={0} className={pageStyles.resultContainer}>
                  <Typography gutterBottom variant="h6">
                    Result
                  </Typography>
                  {errorMessage === "" ? (
                    <Grid container spacing={3}>
                      <Grid item xs={12} className={pageStyles.successMessage}>
                        <Typography gutterBottom variant="h5" align="center">
                          Phone is valid
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography gutterBottom variant="body1" align="center">
                          Country Code
                        </Typography>
                        <Typography gutterBottom variant="h6" align="center">
                          {result.countryCode}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={6} lg={6}>
                        <Typography gutterBottom variant="body1" align="center">
                          Phone Number
                        </Typography>
                        <Typography gutterBottom variant="h6" align="center">
                          {result.phoneNumber}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={6} lg={6}>
                        <Typography gutterBottom variant="body1" align="center">
                          National Format
                        </Typography>
                        <Typography gutterBottom variant="h6" align="center">
                          {result.nationalFormat}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={6} lg={6}>
                        <Typography gutterBottom variant="body1" align="center">
                          Mobile Country Code
                        </Typography>
                        <Typography gutterBottom variant="h6" align="center">
                          {result.carrier.mobileCountryCode}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={6} lg={6}>
                        <Typography gutterBottom variant="body1" align="center">
                          Mobile Network Code
                        </Typography>
                        <Typography gutterBottom variant="h6" align="center">
                          {result.carrier.mobileNetworkCode}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={6} lg={6}>
                        <Typography gutterBottom variant="body1" align="center">
                          Name
                        </Typography>
                        <Typography gutterBottom variant="h6" align="center">
                          {result.carrier.name}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={6} lg={6}>
                        <Typography gutterBottom variant="body1" align="center">
                          Type
                        </Typography>
                        <Typography gutterBottom variant="h6" align="center">
                          {result.carrier.type}
                        </Typography>
                      </Grid>
                    </Grid>
                  ) : (
                    <Box className={pageStyles.errorMessage}>
                      <Typography gutterBottom variant="h6" align="center">
                        <HtmlContent dangerouslySetInnerHTML={{ __html: errorMessage }}></HtmlContent>
                      </Typography>
                    </Box>
                  )}
                  <Box p={3}>&nbsp;</Box>
                </Paper>
              </>
            ) : null}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    validatorContainer: {
      padding: theme.spacing(1),
      marginBottom: theme.spacing(4),
      height: "100%",
    },
    inputContainer: {
      color: theme.palette.type === "dark" ? "#ffffff" : "#000000",
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(2),
      margin: theme.spacing(0),
      height: "calc(100% - 70px)",
    },
    resultContainer: {
      backgroundColor: theme.palette.type === "dark" ? theme.palette.grey[800] : theme.palette.grey[100],
      padding: 8,
    },
    errorMessage: {
      color: theme.palette.error.main,
    },
    successMessage: {
      color: theme.palette.success.main,
    },
  })
);

export default PhoneValidator;
